import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, debounceTime, firstValueFrom, Observable } from 'rxjs';
import { IMethod, IToggleWatchlistResponse, IWatchlistRender, IWatchlistStateResponse, TransactionType } from '../../models';
import { getMessageFromError } from '../../models/error';
import { IError } from '../../providers/basic/error.service';
import { AuthProvider, ComponentProvider } from '../../providers';
import { MethodsService } from '../methods/methods.service';
import { ToastService } from '../toast.service';

@Injectable({
  providedIn: 'root'
})
export class WatchlistService {
  // Properties
  /** BehaviorSubject to hold the watchlist data */
  private readonly watchlist: BehaviorSubject<IWatchlistRender | null> = new BehaviorSubject<IWatchlistRender | null>(null);
  /** Observable to watch for changes in the watchlist */
  public shoppingCart$ = this.watchlist.asObservable();
  /** Observable to watch for changes in the message */
  private readonly message: BehaviorSubject<string> = new BehaviorSubject<string>('');
  /** Observable to watch for changes in the message */
  public message$ = this.message.asObservable();

  // References
  TransactionTypeEnum = TransactionType;

  // Services
  private readonly authService = inject(AuthProvider);
  private readonly componentService = inject(ComponentProvider);
  private readonly methodsService = inject(MethodsService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  // Getters & Setters
  /** Get the amount of items in the watchlist */
  get amount(): number {
    return this.authService.cookies(true).basket?.watchListCount ?? 0;
  }

  // Methods
  async init() {
    const response = await firstValueFrom(this.componentService.get<IWatchlistRender, any>('Watchlist').pipe(debounceTime(500)));
    this.watchlist.next(response);
  }

  /**
   * Ensures that the headers for the given method are set with the provided article ID and article key ID.
   * If the headers are already set, the method is returned as is.
   *
   * @param method - The method object that may or may not have headers set.
   * @param articleID - The ID of the article to be included in the headers.
   * @param articleKeyID - The key ID of the article to be included in the headers.
   * @returns The method object with headers set if they were not already present.
   * @deprecated Do not set headers (articleID) manually anymore. The backend response is providing the corrrect method with headers.
   */
  protected ensureHeaders(method: IMethod, articleID: number): IMethod {
    if (method.headers) return method;
    console.warn('ShoppingCartService: ensureHeaders is deprecated and will be removed in future versions.');
    method.headers = {
      code: {
        ArticleID: articleID
      }
    };
    return method;
  }

  /**
   * Adds an item to the shopping cart or to the wish list depending on the method provided.
   * @param articleID The ID of the article to be added.
   * @param articleKeyID The key ID of the article to be added.
   * @param method The backend method to call for adding the item.
   * @param transactionType The transaction type (e.g., purchase, rental) for the item.
   * @param quantity Optional quantity of the item to be added.
   * @param voucherValue Optional voucher value to be applied to the item.
   * @param payloadVoucher Optional additional payload for the voucher.
   */
  async toggleWatchlistItem(
    articleID: number,
    articleKeyID: number,
    method: IMethod,
    transactionType: TransactionType,
    quantity?: number,
    voucherValue?: number,
    payloadVoucher?: Record<string, any>
  ) {
    try {
      let params: any = {
        id: articleKeyID,
        type: transactionType
      };
      if (quantity) params.quantity = quantity;

      if (voucherValue !== undefined) {
        params.fixedPrice = voucherValue;
      }

      const response = await firstValueFrom(
        this.methodsService.callMethod<any>(this.ensureHeaders(method, articleID), params, payloadVoucher, null)
      );
      if (response?.error) {
        this.showError(response);
        return response;
      }
      this.init();
      return response;
    } catch (error) {
      this.showError(error);
      return error;
    }
  }

  /**
   * Toggles the watchlist entry of an article from a product tile. The backend resolves the
   * default variant, so the tile does not have to know an article key (CS4-1278).
   * @param method The `toggleWatchList` method delivered with the component render
   * @param articleID The article of the clicked tile
   * @returns The new watchlist state, or undefined if the call was not possible or failed
   */
  async toggleWatchlistArticle(method: IMethod | undefined, articleID: number): Promise<boolean | undefined> {
    if (!method) return undefined;
    try {
      const response = await firstValueFrom(this.methodsService.callMethod<IToggleWatchlistResponse>(method, { id: articleID }));
      if (response?.error) {
        this.showError(response);
        return undefined;
      }
      this.init();
      // Missing field keeps the current state instead of falling back to "not on the watchlist"
      return typeof response?.inWatchlist === 'boolean' ? response.inWatchlist : undefined;
    } catch (error) {
      this.showError(error);
      return undefined;
    }
  }

  /**
   * Loads the watchlist state for the product tiles of a component (CS4-1278).
   *
   * The state hangs on the visitor, the component render lands in the shared page cache - so it
   * must not travel with the search hits. This call goes through the component API and is never
   * cached (see Docs/KomponentenCache.md in Projects3).
   * @param method The `getWatchlistState` method delivered with the component render
   * @returns The article and variant IDs on the watchlist, or undefined if the call was not possible
   */
  async loadWatchlistState(method: IMethod | undefined): Promise<{ articleIDs: Set<number>; articleKeyIDs: Set<number> } | undefined> {
    if (!method) return undefined;
    try {
      const response = await firstValueFrom(this.methodsService.callMethod<IWatchlistStateResponse>(method));
      if (response?.error) return undefined;
      return {
        articleIDs: new Set(response?.articleIDs ?? []),
        articleKeyIDs: new Set(response?.articleKeyIDs ?? [])
      };
    } catch {
      // A failed state call must not break the tiles - the hearts just stay empty until the next
      // toggle, which answers with the state of its own.
      return undefined;
    }
  }

  /**
   * Shows a failed watchlist call to the user. Without it the toggle silently does nothing,
   * because the callers only react to a successful response.
   * @param error The error of the call, either a converted IError or a response carrying an error
   */
  private showError(error: unknown): void {
    // Die Meldung des Backends steckt im error-Teil, der Rest ist die generische HTTP-Meldung
    const message = getMessageFromError((error as IError)?.error) ?? getMessageFromError(error);
    this.toastService.showToast(
      this.translateService.instant('components.articleInfo.shared.error'),
      message ?? this.translateService.instant('components.articleInfo.shared.watchlistError'),
      'error',
      5000
    );
  }

  /**
   * Updates the quantity for a watchlist item.
   * Mirrors the ShoppingCart update flow: call backend -> refresh watchlist.
   *
   * @param render The current watchlist render (to access backend methods).
   * @param itemId The watchlist item ID.
   * @param quantity The new quantity.
   * @param payload Optional payload (e.g. full item data) if backend expects it.
   * @returns Observable of backend response
   */
  updateItemQuantity(
    render: IWatchlistRender | null | undefined,
    itemId: number,
    quantity: number,
    payload?: any
  ): Observable<any> | undefined {
    const method: any = (render as any)?.methods?.update;
    if (!method) return undefined;

    const body = payload ? { ...payload, iD: itemId, quantity } : { iD: itemId, quantity };

    // NOTE: params/context are unknown in your snippet -> keep minimal and non-breaking
    return this.methodsService.callMethod<any>(method, null, body, { id: itemId });
  }

  /**
   * Updates the local watchlist BehaviorSubject (optional helper).
   * This allows immediate UI feedback without waiting for init() if you want it.
   *
   * @param itemId The watchlist item ID.
   * @param quantity The new quantity.
   */
  patchLocalQuantity(itemId: number, quantity: number): void {
    const current = this.watchlist.value;
    if (!current?.items?.data) return;

    const updatedItems = current.items.data.map((it: any) => (it.iD === itemId ? { ...it, quantity } : it));
    this.watchlist.next({
      ...current,
      items: {
        ...current.items,
        data: updatedItems
      }
    } as any);
  }
}
